export type PolicyBlock = {
  heading: string;
  paragraphs: string[];
  list?: Array<{ label?: string; text: string }>;
};

const PRIVACY_EN: PolicyBlock[] = [
  {
    heading: '1. Introduction',
    paragraphs: [
      'In this policy, “we”, “us”, and “our” refer to NextCompli and the NextCompli website and platform services.',
      'This policy applies when NextCompli acts as a data controller for website visitors, service users, email recipients, and personal data sourced from public records.',
      'When NextCompli processes personal data on behalf of customers as a processor, that processing is governed by customer agreements and the applicable data processing terms.',
    ],
  },
  {
    heading: '2. How we use personal data',
    paragraphs: [
      'We process personal data to operate and secure our services, provide support, communicate with users, and improve product performance.',
      'Depending on the processing context, legal bases include contract performance, consent, legitimate interests, and compliance with legal obligations.',
    ],
    list: [
      { label: 'Usage data:', text: 'IP address, browser type, pages viewed, and interaction events used for analytics and service improvement.' },
      { label: 'Account data:', text: 'Name, email, company details, authentication credentials, and billing details used to provide and secure service access.' },
      { label: 'Service data:', text: 'Data entered or generated while using the platform, processed to deliver product functionality.' },
      { label: 'Enquiry and correspondence data:', text: 'Information submitted through forms, support channels, and email communication.' },
      { label: 'Public data:', text: 'Professional profile details obtained from publicly available sources for business outreach where permitted.' },
    ],
  },
  {
    heading: '3. Sources of personal data',
    paragraphs: [
      'We primarily receive personal data directly from you, your organization, and integrations you authorize. We may also collect limited professional data from publicly available sources.',
    ],
  },
  {
    heading: '4. Data sharing',
    paragraphs: [
      'We may share personal data with affiliated entities, infrastructure providers, analytics and support vendors, legal and professional advisers, and other processors that help us operate the service.',
      'All such sharing is limited to purposes described in this policy and subject to contractual, technical, and organizational safeguards.',
    ],
  },
  {
    heading: '5. International transfers',
    paragraphs: [
      'NextCompli may process or transfer personal data internationally where required for service delivery. For transfers outside jurisdictions with adequacy decisions, we use safeguards such as standard contractual clauses and transfer-risk controls.',
    ],
  },
  {
    heading: '6. Retention and deletion',
    paragraphs: [
      'We retain personal data for as long as needed to provide services, meet contractual obligations, resolve disputes, and comply with law.',
      'When data is no longer needed, we delete or anonymize it. Some records may be retained longer for tax, audit, fraud prevention, and legal compliance purposes.',
    ],
  },
  {
    heading: '7. External links',
    paragraphs: [
      'Our website may link to third-party websites. This policy applies only to NextCompli websites and services. Third-party sites have their own privacy policies and practices.',
    ],
  },
  {
    heading: '8. Automated decision-making',
    paragraphs: [
      'NextCompli does not use fully automated decision-making that produces legal or similarly significant effects on individuals without appropriate human oversight.',
    ],
  },
  {
    heading: '9. Data subject rights',
    paragraphs: [
      'Depending on applicable law, you may have rights to access, correct, delete, restrict, object to processing, request portability, and withdraw consent where consent is the legal basis.',
      'You may also lodge a complaint with the supervisory authority in your jurisdiction if you believe your rights have been violated.',
    ],
  },
  {
    heading: '10. Contact us',
    paragraphs: [
      'For privacy requests, please contact NextCompli support through the website contact form or your designated account channel. We may request verification before fulfilling rights requests.',
    ],
  },
  {
    heading: '11. Policy updates',
    paragraphs: [
      'We may update this policy from time to time by posting a revised version on this page. Material changes may also be communicated through service notifications or email.',
    ],
  },
  {
    heading: '12. Reference guidance',
    paragraphs: ['Our privacy program references globally recognized privacy-risk and response resources.'],
  },
];

const PRIVACY_ES: PolicyBlock[] = [
  {
    heading: '1. Introducción',
    paragraphs: [
      'En esta política, “nosotros” se refiere a NextCompli y a los servicios del sitio web y de la plataforma NextCompli.',
      'Esta política aplica cuando NextCompli actúa como responsable del tratamiento de visitantes del sitio, usuarios del servicio, destinatarios de correo y datos personales obtenidos de registros públicos.',
      'Cuando NextCompli trata datos personales en nombre de clientes como encargado, ese tratamiento se rige por los acuerdos con el cliente y los términos de procesamiento de datos aplicables.',
    ],
  },
  {
    heading: '2. Cómo usamos los datos personales',
    paragraphs: [
      'Tratamos datos personales para operar y asegurar nuestros servicios, brindar soporte, comunicarnos con usuarios y mejorar el rendimiento del producto.',
      'Según el contexto, las bases legales incluyen ejecución de contrato, consentimiento, intereses legítimos y cumplimiento de obligaciones legales.',
    ],
    list: [
      { label: 'Datos de uso:', text: 'Dirección IP, tipo de navegador, páginas vistas y eventos de interacción usados para analítica y mejora del servicio.' },
      { label: 'Datos de cuenta:', text: 'Nombre, correo, datos de empresa, credenciales de autenticación y facturación para proporcionar y asegurar el acceso.' },
      { label: 'Datos del servicio:', text: 'Datos ingresados o generados al usar la plataforma, tratados para entregar la funcionalidad del producto.' },
      { label: 'Consultas y correspondencia:', text: 'Información enviada por formularios, canales de soporte y correo.' },
      { label: 'Datos públicos:', text: 'Detalles de perfil profesional obtenidos de fuentes públicas para contacto comercial cuando esté permitido.' },
    ],
  },
  {
    heading: '3. Fuentes de datos personales',
    paragraphs: [
      'Principalmente recibimos datos personales de ti, tu organización e integraciones que autorizas. También podemos recopilar datos profesionales limitados de fuentes públicas.',
    ],
  },
  {
    heading: '4. Compartir datos',
    paragraphs: [
      'Podemos compartir datos personales con entidades afiliadas, proveedores de infraestructura, analítica y soporte, asesores legales y otros encargados que ayudan a operar el servicio.',
      'Todo intercambio se limita a los fines descritos en esta política y está sujeto a salvaguardas contractuales, técnicas y organizativas.',
    ],
  },
  {
    heading: '5. Transferencias internacionales',
    paragraphs: [
      'NextCompli puede tratar o transferir datos personales internacionalmente cuando se requiera para prestar el servicio. Fuera de jurisdicciones con decisión de adecuación, usamos salvaguardas como cláusulas contractuales tipo y controles de riesgo de transferencia.',
    ],
  },
  {
    heading: '6. Conservación y eliminación',
    paragraphs: [
      'Conservamos datos personales el tiempo necesario para prestar servicios, cumplir contratos, resolver disputas y cumplir la ley.',
      'Cuando ya no se necesitan, los eliminamos o anonimizamos. Algunos registros pueden conservarse más tiempo por impuestos, auditoría, prevención de fraude y cumplimiento legal.',
    ],
  },
  {
    heading: '7. Enlaces externos',
    paragraphs: [
      'Nuestro sitio puede enlazar a sitios de terceros. Esta política aplica solo a sitios y servicios de NextCompli. Los sitios de terceros tienen sus propias políticas de privacidad.',
    ],
  },
  {
    heading: '8. Decisiones automatizadas',
    paragraphs: [
      'NextCompli no usa toma de decisiones plenamente automatizada que produzca efectos jurídicos o de impacto similar sobre personas sin supervisión humana adecuada.',
    ],
  },
  {
    heading: '9. Derechos de las personas',
    paragraphs: [
      'Según la ley aplicable, puedes tener derechos de acceso, corrección, eliminación, limitación, oposición, portabilidad y retiro del consentimiento cuando esa sea la base legal.',
      'También puedes presentar una queja ante la autoridad de control de tu jurisdicción si crees que se vulneraron tus derechos.',
    ],
  },
  {
    heading: '10. Contáctanos',
    paragraphs: [
      'Para solicitudes de privacidad, contacta el soporte de NextCompli por el formulario del sitio o tu canal de cuenta designado. Podemos pedir verificación antes de atender solicitudes de derechos.',
    ],
  },
  {
    heading: '11. Actualizaciones de la política',
    paragraphs: [
      'Podemos actualizar esta política publicando una versión revisada en esta página. Los cambios materiales también pueden comunicarse por notificaciones del servicio o correo.',
    ],
  },
  {
    heading: '12. Guía de referencia',
    paragraphs: ['Nuestro programa de privacidad referencia recursos reconocidos de riesgo y respuesta en privacidad.'],
  },
];

const TERMS_EN: PolicyBlock[] = [
  {
    heading: '1. Acceptance and scope',
    paragraphs: [
      'By creating an account, logging in, or using NextCompli services, you acknowledge and agree to these Terms of Service and related policies referenced here.',
      'Only authorized users may access protected dashboard and account features. You are responsible for account security, credential confidentiality, and lawful use of the platform.',
    ],
  },
  {
    heading: '2. Acceptable use and restricted activities',
    paragraphs: [
      'You may not use NextCompli to violate law, bypass sanctions controls, infringe intellectual property rights, or interfere with service integrity and security operations.',
      'NextCompli may suspend or restrict access for suspected prohibited use, abuse, fraud, or security risk events.',
    ],
  },
  {
    heading: '3. Billing, renewal, and cancellation',
    paragraphs: [
      'Subscription plans, billing frequency, renewal behavior, and cancellation terms are disclosed in your applicable order form or service plan context.',
      'Unless otherwise stated in the applicable order form, subscriptions renew according to the plan cycle, and cancellation requests apply to future billing periods after processing.',
      'For production agreements, enterprise terms, and DPA documents, request contract materials through the contact page.',
    ],
    list: [
      { label: 'Self-serve checkout:', text: 'Renewal timing and cancellation windows are displayed before order confirmation.' },
      { label: 'Sales-assisted checkout:', text: 'Renewal and cancellation terms are disclosed in quote/order documentation.' },
      { label: 'Partner-assisted checkout:', text: 'Renewal and cancellation obligations are stated in partner order artifacts.' },
    ],
  },
  {
    heading: '4. Electronic records and notices',
    paragraphs: [
      'Where permitted by law, records, notices, and communications may be provided electronically. You agree to maintain accurate contact details and to retain copies of contract and policy records for your internal governance needs.',
    ],
  },
  {
    heading: '5. Policy changes',
    paragraphs: [
      'NextCompli may update these terms from time to time. Material updates are reflected with a revised version identifier and updated date on this page.',
    ],
  },
  {
    heading: '6. Contact and legal requests',
    paragraphs: [
      'For legal terms, compliance requests, and contract documentation support, use the website contact route and your designated account channel.',
    ],
  },
  {
    heading: '7. Governing law and venue',
    paragraphs: [
      'These terms are governed by applicable United States and state contract law principles, without regard to conflict-of-law rules, unless otherwise required by non-waivable law.',
      'Unless otherwise agreed in a signed order form, disputes arising from these terms are handled in the courts of competent jurisdiction where NextCompli maintains principal operations.',
    ],
  },
  {
    heading: '8. Dispute resolution process',
    paragraphs: [
      'Before formal proceedings, parties should attempt to resolve disputes through written notice and a good-faith escalation period.',
      'Where permitted, either party may seek equitable relief for misuse of confidential information, security abuse, or intellectual property violations.',
    ],
  },
];

const TERMS_ES: PolicyBlock[] = [
  {
    heading: '1. Aceptación y alcance',
    paragraphs: [
      'Al crear una cuenta, iniciar sesión o usar los servicios de NextCompli, reconoces y aceptas estos Términos del Servicio y las políticas relacionadas aquí referenciadas.',
      'Solo usuarios autorizados pueden acceder a funciones protegidas de panel y cuenta. Eres responsable de la seguridad de la cuenta, la confidencialidad de credenciales y el uso lícito de la plataforma.',
    ],
  },
  {
    heading: '2. Uso aceptable y actividades restringidas',
    paragraphs: [
      'No puedes usar NextCompli para violar la ley, eludir controles de sanciones, infringir propiedad intelectual ni interferir con la integridad del servicio y las operaciones de seguridad.',
      'NextCompli puede suspender o restringir el acceso ante uso prohibido, abuso, fraude o eventos de riesgo de seguridad.',
    ],
  },
  {
    heading: '3. Facturación, renovación y cancelación',
    paragraphs: [
      'Los planes de suscripción, la frecuencia de facturación, la renovación y la cancelación se divulgan en tu orden aplicable o en el contexto del plan de servicio.',
      'Salvo que la orden indique otra cosa, las suscripciones se renuevan según el ciclo del plan, y las cancelaciones aplican a periodos de facturación futuros después de procesarse.',
      'Para acuerdos de producción, términos empresariales y documentos de DPA, solicita materiales contractuales en la página de contacto.',
    ],
    list: [
      { label: 'Checkout de autoservicio:', text: 'Los plazos de renovación y ventanas de cancelación se muestran antes de confirmar el pedido.' },
      { label: 'Checkout asistido por ventas:', text: 'Los términos de renovación y cancelación se divulgan en la cotización u orden.' },
      { label: 'Checkout asistido por socio:', text: 'Las obligaciones de renovación y cancelación constan en artefactos de pedido del socio.' },
    ],
  },
  {
    heading: '4. Registros y avisos electrónicos',
    paragraphs: [
      'Cuando la ley lo permita, registros, avisos y comunicaciones pueden entregarse electrónicamente. Aceptas mantener datos de contacto precisos y conservar copias de contratos y políticas para tu gobernanza interna.',
    ],
  },
  {
    heading: '5. Cambios de política',
    paragraphs: [
      'NextCompli puede actualizar estos términos. Las actualizaciones materiales se reflejan con un identificador de versión revisado y la fecha actualizada en esta página.',
    ],
  },
  {
    heading: '6. Contacto y solicitudes legales',
    paragraphs: [
      'Para términos legales, solicitudes de cumplimiento y documentación contractual, usa la ruta de contacto del sitio y tu canal de cuenta designado.',
    ],
  },
  {
    heading: '7. Ley aplicable y foro',
    paragraphs: [
      'Estos términos se rigen por principios aplicables de derecho contractual de Estados Unidos y estatales, sin considerar normas de conflicto de leyes, salvo que una ley no renunciable lo exija.',
      'Salvo acuerdo distinto en una orden firmada, las disputas se tramitan en los tribunales competentes donde NextCompli mantiene sus operaciones principales.',
    ],
  },
  {
    heading: '8. Proceso de resolución de disputas',
    paragraphs: [
      'Antes de procedimientos formales, las partes deben intentar resolver disputas mediante aviso escrito y un periodo de escalamiento de buena fe.',
      'Cuando esté permitido, cualquiera de las partes puede buscar medidas equitativas por uso indebido de información confidencial, abuso de seguridad o violaciones de propiedad intelectual.',
    ],
  },
];

export const POLICY_SECTIONS = {
  privacy: { en: PRIVACY_EN, es: PRIVACY_ES },
  terms: { en: TERMS_EN, es: TERMS_ES },
} as const;
